import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Grid from '@mui/material/Grid';
import moment from 'moment';
import Rockwood from '../clinicalFrailtyScore/rockwood';
import { Triage } from './triage';
import Household from '../household';
import { useDispatch, useSelector } from 'react-redux';
import { hasPermission, Permissions, user } from '../../../../store/reducers/user';
import _ from 'lodash';
import { addWorkflowStep, nextWorkflowStep, setWorkflow, workflow } from '../../../../store/reducers/workflow';
import Delirium from '../delirium/delirium';
import MultipleEntriesFormList from '../../../../components/forms/multipleEntriesFormList';
import { additionalData, setLoading, updatePatientAdditionalData } from '../../../../store/reducers/additionalData';
import PaediatricSafeguarding from '../safeguarding/paediatricSafeguarding';
import keyPerformanceIndicator from '../../../../util/timeMarker';
import ObservationTable from '../../../../components/observation/observationTable';
import Notes from '../../../../util/notes';
import Formatter from '../../../../util/formatter';
import uuidv4 from 'uuid/v4';
import { setSnackbar } from '../../../../store/reducers/globals';
import MessageBox from '../../../../util/messageBox';
import useOpenState from '../../../../hooks/useOpenState';
import API from '../../../../app/api';
import { addNoteForPatient } from '../../../../store/reducers/notes';
import { patientBanner } from '../../../../controls/agyle/tasks/components/patientBanner';
import AgyleButton from '../../../../components/ui/buttons/agyleButton';
import AgyleDialog from '../../../../components/modal/agyleDialog';
import InformationBox from '../../../../components/other/informationBox';
import AgyleAccordion from '../../../../components/ui/accordion/agyleAccordion';
import {
    getSafeguardingLevel,
    haveClinicalFrailtyScore,
    isCauseForConcernFormComplete,
    isMentalHealthFormComplete,
    isMentalHealthFormNeeded,
    isOver65Patient,
    PATIENT_AGE_TYPE,
    PATIENT_CONTEXT_TYPE,
} from '../../../../util/patientContextUtil';
import { addOrReplace, getPatientFlagValue } from '../../mainScreen/util/patientFlagsUtil';
import { getSafeguardingValues } from '../formsUtil';
import { getConfig } from '../../../../util/configUtil';
import { ambulanceCodes } from '../../../../store/reducers/dicts';
import { openPatientForm } from '../../../../store/reducers/patientContext';
import clsx from 'clsx';
import { WORKFLOW_OPTIONS } from '../../../../util/workflowUtil';
import ReferralReviewButton from '../referralReview/referralReviewButton';
import { isMTSVersion } from '../../../../components/forms/acuityScore/acuityScoreUtils';
import InitialAssessmentSummary from '../initialAssessment/initialAssessmentSummary';

const TriageForm = (props) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    // Selectors
    const workflowData = useSelector(workflow);
    const patientAddData = useSelector(additionalData);
    const ambulance = useSelector(ambulanceCodes);
    const userObject = useSelector(user);
    const tester = Formatter.formatUserFullName(userObject);

    // Others
    const [isSmall, setIsSmall] = useState(false);

    useEffect(() => {
        setIsSmall(props._containerWidth < 730);
    }, [props._containerWidth]);

    const lastNutritionValue = _.last(_.filter(patientAddData?.other?.nutritionScreen, (el) => !el.canceled && !_.isEmpty(el.weight)));

    useEffect(() => {
        if (props.open) {
            dispatch(setLoading(false));
        } else {
            setTriageObject(null);
            setTriageChange(false);
            setFormIsModified(props.isActionMode);
        }
    }, [props.open]);

    // Other forms states
    const [triageObject, setTriageObject] = useState(null);
    const [injury, setInjury] = useState(null);
    const [delirium, setDelirium] = useState(null);
    const [rockwood, setRockwood] = useState(null);
    const [paedSafeguarding, setPaedSafeguarding] = useState(null);
    const [flags, setFlags] = useState();

    // Escort
    const [escortList, setEscortList] = useState(null);
    const [escortType, setEscortType] = useState(null);
    const cwiltedId = useRef(null);

    // Snackbar
    const [isError, setIsError] = useState(false);

    // Date/Time Pickers Error
    const [errorPicker, setErrorPicker] = useState(null);
    const [injuryErrorPicker, setInjuryErrorPicker] = useState(null);

    const [fullFormMode, setFullFormMode] = useState(!props.isActionMode);

    const [formIsModified, setFormIsModified] = useState(props.isActionMode);

    const [triageChange, setTriageChange] = useState(false);

    // Open states
    const [isRemoveTimestampOpen, openRemoveTimestamp, closeRemoveTimestamp] = useOpenState(false, true);
    const [isConfirmCloseOpen, openConfirmClose, closeConfirmClose] = useOpenState(false, true);

    // Configs
    const escortInTriage = getConfig('escortInTriage', false);
    const enableUnSaveChangeWarning = getConfig('enableUnSaveChangeWarning', false);
    const allergiesMandatory = getConfig('allergiesMandatory', false);
    const triageObservationMandatory = getConfig('triageObservationMandatory', false);
    const ambulanceHandoverDoneWithTriage = getConfig('ambulanceHandoverDoneWithTriage', false);
    const safeguardingLevelMandatory = getConfig('safeguardingLevelMandatory', false);
    const CFSMandatory = getConfig('CFSMandatory', false);
    const causeForConcernMandatory = getConfig('causeForConcernMandatory', false);
    const triageTreatmentForm = getConfig('triageTreatmentForm', false);
    const referralReviewForm = getConfig('referralReviewForm', false);
    const mandatoryTriageCC = getConfig('mandatoryTriageCC', true);

    // Others
    const hasSRFlag = getPatientFlagValue(props.patient, 'S&R');

    useEffect(() => {
        if (injury || delirium || rockwood || flags || triageChange || paedSafeguarding) setFormIsModified(true);
    }, [injury, triageChange, delirium, rockwood, flags, paedSafeguarding]);

    useEffect(() => {
        if (props.onCustomButtons) props.onCustomButtons(buttons());
    }, [props.patient, fullFormMode, isSmall]);

    useEffect(() => {
        if (props.agyleDialogInsertButtons) props.agyleDialogInsertButtons(buttons());

        if (props.onSubmitFunction) {
            props.onSubmitFunction(sendChange);
        }
    }, [
        triageObject,
        props.patient,
        props.patientAgeType,
        ambulanceHandoverDoneWithTriage,
        errorPicker,
        allergiesMandatory,
        injury,
        injuryErrorPicker,
        escortList,
        flags,
        tester,
        paedSafeguarding,
        delirium,
        rockwood,
        lastNutritionValue,
        props.workflowDisabled,
        fullFormMode,
    ]);

    useEffect(() => {
        if (escortInTriage) {
            let lastInjuryForm = null;
            let injuryForm = props?.patient?.studies?.injury?.list;
            if (!_.isEmpty(injuryForm)) {
                injuryForm = _.filter(injuryForm, (form) => !form?.canceled);
                lastInjuryForm = _.first(injuryForm);
            }

            if (lastInjuryForm) {
                setEscortType('injury');
                cwiltedId.current = lastInjuryForm?.id;
                setEscortList(lastInjuryForm?.cwilted?.escort || []);
            } else if (props.patient?.metadata?.escort) {
                setEscortType('admit');
                setEscortList(props.patient?.metadata?.escort);
            } else {
                setEscortList([]);
            }
        }
    }, [props.patient]);

    useEffect(() => {
        if (fullFormMode && props.onModify) {
            props.onModify(formIsModified);
        }
    }, [formIsModified, fullFormMode]);

    const getTriageNote = () => {
        return Notes.getTriageNote(triageObject, props.patientAgeType, false, escortList);
    };
    const getTriageActionNote = () => {
        return Notes.getActionNote('Triage', triageObject);
    };

    const handleClose = (_e, reason) => {
        if (reason !== 'backdropClick') {
            if (props.setAmbulanceHandOverOpened) props.setAmbulanceHandOverOpened(false);
            if (enableUnSaveChangeWarning && formIsModified && (!props.isActionMode || (props.isActionMode && fullFormMode)))
                openConfirmClose();
            else props.onClose();
        }
    };

    const setMainTriage = () => {
        let clone = _.cloneDeep(triageObject);

        let clerkingInvestigations = props.patient?.clerkingObject?.investigations || [];
        let triageInvestigations = clone?.investigationsTriage || [];
        let mainInvestigations = clone?.investigations || [];

        let clerkingTreatment = props.patient?.clerkingObject?.treatmentAndManagementPlan || [];
        let triageTreatment = clone?.treatmentAndManagementPlanTriage || [];
        let mainTreatment = clone?.treatmentAndManagementPlan || [];

        // AGL-1244
        // first time triage, clerking clinician and acuity needs to be in chronological order in ECDS
        if (_.isNil(clone?.firstWho) || moment(clone.firstWhen).isAfter(moment(clone.when))) {
            clone = {
                ...clone,
                firstWho: clone.who,
                firstWhoUserName: clone.whoUserName,
                firstWhen: clone.when,
                firstDiscriminator: clone.discriminator,
            };
        }

        let uniq = [];

        [...mainInvestigations, ...clerkingInvestigations, ...triageInvestigations].forEach((item) => {
            if (!uniq.some((uniqItem) => uniqItem.value?.__code === item.value?.__code)) {
                uniq.push(item);
            }
        });

        let uniqTreatment = [];
        [...mainTreatment, ...clerkingTreatment, ...triageTreatment].forEach((item) => {
            if (!uniqTreatment.some((uniqItem) => uniqItem.value?.__code === item.value?.__code)) {
                uniqTreatment.push(item);
            }
        });

        clone['dependency'] = clone.dependencyTriage;

        return {
            ...clone,
            investigations: uniq,
            treatmentAndManagementPlan: uniqTreatment,
        };
    };

    const validInjuryPickers = () => {
        if (injury) {
            if (injuryErrorPicker) {
                dispatch(
                    setSnackbar({
                        type: 'warning',
                        message: `Date or Time in section ${props.patientAgeType !== PATIENT_AGE_TYPE.PAEDS ? 'Injury' : 'CWILTED'} is invalid. Please check again.`,
                    }),
                );
                return !injuryErrorPicker;
            }
        }
        return true;
    };

    const validTriage = () => {
        const isMST = isMTSVersion(triageChange, props.patient?.admission?.startDate);
        if (
            !triageObject?.when ||
            errorPicker ||
            (isMST ? !triageObject?.presentation : false) ||
            !triageObject?.discriminator ||
            !triageObject?.dependencyTriage ||
            !triageObject?.triageClinicians ||
            (mandatoryTriageCC && !triageObject?.chiefComplaint) ||
            (triageObject?.mentalHealthRisk && !triageObject?.mentalHealthRiskLevel)
        ) {
            setIsError(true);
            dispatch(
                setSnackbar({
                    type: 'warning',
                    message: 'Not all mandatory fields have been filled out!',
                }),
            );
        } else if (!triageObject?.allergies && allergiesMandatory) {
            setIsError(true);
            dispatch(
                setSnackbar({
                    type: 'warning',
                    message: 'Allergies field is mandatory.',
                }),
            );
        } else if (triageObservationMandatory && !triageObject?.observationNotRequired && !triageObject?.savedObservationId) {
            setIsError(true);
            dispatch(
                setSnackbar({
                    type: 'warning',
                    message: 'Observation section is mandatory.',
                }),
            );
        }

        return (
            triageObject?.when &&
            (isMST ? triageObject?.presentation : true) &&
            triageObject?.dependencyTriage &&
            triageObject?.discriminator &&
            !errorPicker &&
            triageObject?.triageClinicians &&
            ((triageObject?.allergies && allergiesMandatory) || !allergiesMandatory) &&
            ((triageObservationMandatory && (triageObject?.observationNotRequired || triageObject?.savedObservationId)) ||
                !triageObservationMandatory) &&
            ((triageObject?.chiefComplaint && mandatoryTriageCC) || !mandatoryTriageCC) &&
            !(triageObject?.mentalHealthRisk && !triageObject?.mentalHealthRiskLevel)
        );
    };

    const validTimestamp = () => {
        const isValid = !_.isNil(triageObject.triageClinicians) && triageObject.when && moment(triageObject.when).isValid();
        setIsError(!isValid);
        return isValid;
    };

    const getSafeguardingValuesTriage = () => {
        let sTriage = triageObject?.safeguarding ? triageObject.safeguarding : [];
        let sAll = getSafeguardingValues(props.patient);

        let sPaedsSafeguarding = [];

        if (paedSafeguarding) {
            _.forEach(paedSafeguarding[0].value.list, (element) => {
                sPaedsSafeguarding.push(...element?.safeguarding?.reasons?.safeguarding);
            });
        }

        return _.uniqWith([...sAll, ...sTriage, ...sPaedsSafeguarding], _.isEqual);
    };

    function calculatePtWeight(weight, units) {
        if (units !== 'kg') {
            return units === 'st' ? _.toString(Number(weight) * 6.35) : _.toString(Number(weight) * 0.45);
        }
        return weight;
    }

    const canMakeAmbulanceHandover = () =>
        !props?.patient?.ambulanceHandover &&
        props?.patient?.wardId !== PATIENT_CONTEXT_TYPE.EDAU &&
        ambulance?.includes(props?.patient?.admission?.arrivalModeCode);

    const sendChange = () => {
        if (validTimestamp() && !fullFormMode) {
            const triageValue = setMainTriage();

            let modifies = [
                {
                    name: 'triage',
                    value: triageValue,
                    noteToAdd: getTriageActionNote(),
                },
            ];
            keyPerformanceIndicator(modifies, props.patient, ['firstInitialAssessment'], {
                when: triageValue.when,
                who: triageValue.who,
            });

            if (ambulanceHandoverDoneWithTriage) {
                if (canMakeAmbulanceHandover()) {
                    modifies.push({
                        name: 'ambulanceHandover',
                        value: {
                            who: triageValue?.triageClinicians,
                            when: triageValue?.when,
                            timestamp: moment().toISOString(),
                        },
                    });
                }
            }

            closeConfirmClose();
            props.onChange(modifies);
        } else if (validTriage() && !errorPicker && validInjuryPickers()) {
            const flagTime = moment().toISOString();
            const triageValue = setMainTriage();

            let modifies = [
                {
                    name: 'triage',
                    value: triageValue,
                    noteToAdd: getTriageNote(),
                },
            ];

            if (ambulanceHandoverDoneWithTriage) {
                if (canMakeAmbulanceHandover()) {
                    modifies.push({
                        name: 'ambulanceHandover',
                        value: {
                            who: triageValue?.triageClinicians,
                            when: triageValue?.when,
                            timestamp: moment().toISOString(),
                        },
                    });
                }
            }

            // Save escort list
            // If no cwilted then save on metadata and save copy from admit no new filed
            // if there is cwilted then update escort on it,
            // if no cwilted and admit escort then create escort field in metadata
            if (escortInTriage && !_.isEmpty(escortList)) {
                switch (escortType) {
                    case 'admit':
                        const clone = _.cloneDeep(props.patient?.metadata?.escort);
                        if (!_.isEqual(clone, escortList)) {
                            // Save copy of original object from admit
                            if (!props.patient?.metadata?.oldEscort) {
                                modifies.push({
                                    name: 'metadata.oldEscort',
                                    value: clone,
                                });
                            }
                            modifies.push({
                                name: 'metadata.escort',
                                value: escortList,
                            });
                        }
                        break;
                    case 'injury':
                        let cloneInjury = _.cloneDeep(props?.patient?.studies?.injury);
                        let cwilted = _.find(cloneInjury?.list, { id: cwiltedId.current });
                        if (cwilted) {
                            cwilted['cwilted']['escort'] = escortList;
                            cloneInjury['by'] = tester;
                            cloneInjury['login'] = userObject?.userName;
                            cloneInjury['date'] = moment().toISOString();

                            modifies.push({
                                name: 'studies.injury',
                                value: cloneInjury,
                            });
                        }
                        break;

                    default:
                        if (!_.isEmpty(escortList)) {
                            modifies.push({
                                name: 'metadata.escort',
                                value: escortList,
                            });
                        }
                        break;
                }
            }

            if (flags) {
                const fitToSit = { name: 'fitToSit', checked: flags.fitToSit, by: tester, date: flagTime };
                const seeAndTreat = { name: 'seeAndTreat', checked: flags.seeAndTreat, by: tester, date: flagTime };
                const inCar = { name: 'inCar', checked: flags.inCar, by: tester, date: flagTime };
                const res = { name: 'res', checked: flags.res, by: tester, date: flagTime };
                const maj = { name: 'maj', checked: flags.maj, by: tester, date: flagTime };
                const amb = { name: 'amb', checked: flags.amb, by: tester, date: flagTime };
                const min = { name: 'min', checked: flags.min, by: tester, date: flagTime };
                const children = { name: 'children', checked: flags.children, by: tester, date: flagTime };
                const utc = { name: 'utc', checked: flags.utc, by: tester, date: flagTime };
                const rats = { name: 'rats', checked: flags.rats, by: tester, date: flagTime };
                const flag111 = {
                    name: '111',
                    checked: flags.flag111?.checked,
                    when111: flags.flag111?.when111,
                    by: tester,
                    date: flagTime,
                };
                let clone = [];

                if (!_.isEmpty(props.patient?.metadata?.patientFlags)) {
                    clone = _.cloneDeep(props.patient.metadata.patientFlags);
                }

                addOrReplace(clone, fitToSit);
                addOrReplace(clone, seeAndTreat);
                addOrReplace(clone, flag111);
                addOrReplace(clone, inCar);
                addOrReplace(clone, maj);
                addOrReplace(clone, res);
                addOrReplace(clone, min);
                addOrReplace(clone, amb);
                addOrReplace(clone, children);
                addOrReplace(clone, utc);
                addOrReplace(clone, rats);

                modifies.push({
                    name: 'metadata.patientFlags',
                    value: clone,
                });
            }

            if (injury) {
                modifies.push(...injury);
            }

            if (props.patientAgeType === PATIENT_AGE_TYPE.PAEDS) {
                if (paedSafeguarding) {
                    modifies.push(...paedSafeguarding);
                }
            }

            if (props.patientAgeType === PATIENT_AGE_TYPE.OVER75) {
                if (delirium) {
                    modifies.push(...delirium);
                }
                if (rockwood) {
                    modifies.push(...rockwood);
                }
            }

            modifies.push({
                name: 'studies.mainScreenSafeguarding',
                value: getSafeguardingValuesTriage(),
            });

            keyPerformanceIndicator(modifies, props.patient, ['firstInitialAssessment'], {
                when: triageValue.when,
                who: triageValue.who,
            });

            // Add new record to nutrition screen when triage pt weight is different from last nutrition record
            if (
                props.patientAgeType === PATIENT_AGE_TYPE.PAEDS &&
                (!lastNutritionValue ||
                    (lastNutritionValue && moment(lastNutritionValue.date).isAfter(props.patient?.admission?.startDate))) &&
                lastNutritionValue?.weight !== triageValue.patientWeight &&
                !_.isEmpty(triageValue.patientWeight)
            ) {
                const listClone = _.cloneDeep(patientAddData?.other?.nutritionScreen || []);
                const nutritionObj = {
                    id: uuidv4() + '',
                    date: moment().toISOString(),
                    by: tester,
                    login: userObject?.userName,
                    bmi: null,
                    bmiResult: '-',
                    weight: calculatePtWeight(triageValue.patientWeight, triageValue.patientWeightUnits),
                    height: null,
                    muac: null,
                    parentForm: 'Triage',
                };

                listClone.push(nutritionObj);

                const data = {
                    nutritionScreen: listClone,
                };
                dispatch(updatePatientAdditionalData(props.patient.patient.__id, data));
            }

            closeConfirmClose();
            props.onChange(modifies);

            if (!props.workflowDisabled) {
                if (
                    safeguardingLevelMandatory &&
                    props.patientAgeType === PATIENT_AGE_TYPE.PAEDS &&
                    !(getSafeguardingLevel(props.patient) || paedSafeguarding)
                )
                    dispatch(addWorkflowStep([WORKFLOW_OPTIONS.SAFEGUARDING]));
                else if (
                    CFSMandatory &&
                    isOver65Patient(props.patient.patient.dob) &&
                    !(haveClinicalFrailtyScore(props.patient) || rockwood)
                )
                    dispatch(addWorkflowStep([WORKFLOW_OPTIONS.CLINICAL_FRAILTY_SCORE]));

                if (isMentalHealthFormNeeded(triageValue) && !isMentalHealthFormComplete(props.patient))
                    dispatch(addWorkflowStep([WORKFLOW_OPTIONS.OPEN_MENTAL_HEALTH_DIALOG]));

                if (causeForConcernMandatory && triageValue.safeguardingConcerns && !isCauseForConcernFormComplete(props.patient))
                    dispatch(addWorkflowStep([WORKFLOW_OPTIONS.OPEN_CAUSE_FOR_CONCERN_DIALOG]));

                if (workflowData.type === WORKFLOW_OPTIONS.SEPSIS_AFTER_TRIAGE) {
                    dispatch(setWorkflow({ type: WORKFLOW_OPTIONS.SEPSIS, data: workflowData.data }));
                } else {
                    dispatch(nextWorkflowStep());
                }
            }
        } else {
            return false;
        }
    };

    const handleRemoveTimestamp = () => {
        let clone = _.cloneDeep(props.patient.triage);
        clone.who = null;
        clone.whoUserName = null;
        clone.when = null;
        clone.triageClinicians = null;
        clone.firstWho = null;
        clone.firstWhoUserName = null;
        clone.firstWhen = null;

        const data = [{ triage: clone }];
        API.updatePatientInWard(props.patient.__id, data)
            .then((res) => {
                if (props.modifyPatient) props.modifyPatient([{ name: 'triage', value: clone }]);
                dispatch(
                    addNoteForPatient(
                        props.patient.patient.__id,
                        {
                            note: Notes.getRemoveTimestampNote('triage', false, false),
                            noteType: 'timestamp',
                            patientsInWardsRef: [props.patient.__id],
                            episodeId: props.patient?.admission?.consultantEpisodeId || null,
                        },
                        props.patient.__id,
                    ),
                );
                dispatch(setSnackbar({ type: 'success', message: 'Timestamp successful removed.' }));
            })
            .catch((err) => {
                console.error(err);
                dispatch(setSnackbar({ type: 'error', message: 'Error while removing triage timestamp.' }));
            })
            .finally(() => {
                closeRemoveTimestamp();
                if (props.onClose) props.onClose();
                if (props._onForceClose) props._onForceClose();
            });
    };

    const [expanded, setExpanded] = React.useState({
        rockwood: false,
        delirium: false,
        injury: false,
        household: false,
        peadSafeguarding: false,
    });

    const canEditTriage = dispatch(hasPermission(Permissions.AGYLE_CAN_MANAGE_TRIAGE)) && !props.disableSave;

    const accordionChangeHandler = (accordion) => (ev) => {
        setExpanded((prev) => ({
            ...prev,
            [accordion]: !prev[accordion],
        }));
    };

    const concatNotes = (pr, form) => {
        if (pr === null) return form;
        form[0].noteToAddWithRefId = form[0].noteToAddWithRefId.concat(pr[0].noteToAddWithRefId);
        return form;
    };

    const setRockwoodForm = (form) => {
        setRockwood((pr) => {
            return concatNotes(pr, form);
        });
    };
    const setDeliriumForm = (form) => {
        setDelirium((pr) => {
            return concatNotes(pr, form);
        });
    };

    const setInjuryForm = (form) => {
        setInjury((pr) => {
            return concatNotes(pr, form);
        });
    };

    const handleChangeEscort = (escort) => {
        setEscortList(escort);
        setTriageChange(!_.isEqual(escort, escortList));
    };

    const setPaedSafeguardingForm = (form) => {
        setPaedSafeguarding((pr) => {
            return concatNotes(pr, form);
        });
    };

    const rockwoodComponent = useMemo(() => {
        return (
            <Grid item xs={12}>
                <AgyleAccordion
                    expanded={expanded.rockwood}
                    onChange={accordionChangeHandler('rockwood')}
                    label={t('Clinical Frailty Score')}
                    formBackground
                >
                    <MultipleEntriesFormList
                        patient={props.patient}
                        type={'rockwood'}
                        title={'Clinical Frailty Score'}
                        field={'studies.rockwood'}
                        showScore
                        triage
                        scoreFiled={'scale.__code'}
                        parentForm={'Triage'}
                        onSave={setRockwoodForm}
                        disableSave={props.disableSave}
                        patientHaveModification={props.patientHaveModification}
                        modifyPatient={props.modifyPatient}
                        component={<Rockwood patient={props.patient} />}
                    />
                </AgyleAccordion>
            </Grid>
        );
    }, [expanded.rockwood]);

    const PeadSafeguardingComponent = useMemo(() => {
        return (
            <Grid item xs={12}>
                <AgyleAccordion
                    expanded={expanded.peadSafeguarding}
                    onChange={accordionChangeHandler('peadSafeguarding')}
                    label={t('Paediatric Safeguarding')}
                    formBackground
                >
                    <MultipleEntriesFormList
                        patient={props.patient}
                        type={'paedSafeguarding'}
                        title={'Paediatric Checks & Level'}
                        field={'studies.paedSafeguarding'}
                        parentForm={'Triage'}
                        onSave={setPaedSafeguardingForm}
                        disableSave={props.disableSave}
                        width={'xl'}
                        triage
                        patientHaveModification={props.patientHaveModification}
                        modifyPatient={props.modifyPatient}
                        component={<PaediatricSafeguarding clerkingObj={triageObject} patient={props.patient} />}
                    />
                </AgyleAccordion>
            </Grid>
        );
    }, [expanded.peadSafeguarding]);

    const DeliriumComponent = useMemo(() => {
        return (
            <Grid item xs={12}>
                <AgyleAccordion
                    expanded={expanded.delirium}
                    onChange={accordionChangeHandler('delirium')}
                    label={t('4AT Assessment Test For Delirium & Cognitive Impairment')}
                    formBackground
                >
                    <MultipleEntriesFormList
                        patient={props.patient}
                        type={'delirium'}
                        title={'4AT Screening for Delirium and Cognitive Impairment'}
                        field={'studies.delirium'}
                        showScore
                        scoreFiled={'totalScore'}
                        parentForm={'Triage'}
                        triage
                        onSave={setDeliriumForm}
                        disableSave={props.disableSave}
                        patientHaveModification={props.patientHaveModification}
                        modifyPatient={props.modifyPatient}
                        component={<Delirium patient={props.patient} />}
                    />
                </AgyleAccordion>
            </Grid>
        );
    }, [expanded.delirium]);

    const HouseholdComponent = useMemo(() => {
        return (
            <Grid item xs={12}>
                <AgyleAccordion
                    expanded={expanded.household}
                    onChange={accordionChangeHandler('household')}
                    label={t('Household Information')}
                    formBackground
                >
                    <Household patient={props.patient} />
                </AgyleAccordion>
            </Grid>
        );
    }, [expanded.household]);

    // const renderAmbulanceHandoverForm = () => (
    //     <div style={{maxWidth: '290px'}}>
    //         <DialogSeen
    //             patient={props.patient}
    //             size="medium"
    //             disableSave={props.disableSave}
    //             ambulanceHandover
    //             fullWidth={false}
    //             allowedConsultants={consultantDictionary.ambulanceHandover}
    //             title={t('Clinician')}
    //             triage
    //             buttonLabel={() => (
    //                 <AgyleButton variant="contained" color="primary">
    //                     {t('Open Ambulance Handover form')}
    //                 </AgyleButton>)}
    //             value={
    //                 props.patient?.ambulanceHandover
    //                     ? props.patient.ambulanceHandover
    //                     : {
    //                         who: null,
    //                         when: moment(),
    //                     }
    //             }
    //             onChange={(value) => {
    //                 const dataToSave = [{name: 'ambulanceHandover', value: value}];
    //                 if (value.when) {
    //                     keyPerformanceIndicator(
    //                         dataToSave,
    //                         props.patient,
    //                         ['firstInitialAssessment'],
    //                         {
    //                             when: value.when,
    //                             who: value.who,
    //                         },
    //                     );
    //                 }
    //                 props.modifyValues(dataToSave);
    //             }}
    //         />
    //     </div>
    // );

    const openAmbulanceHandover = () => {
        dispatch(openPatientForm('ambulanceHandover'));
    };

    const openTreatmentPlan = () => {
        dispatch(openPatientForm('treatmentPlan'));
    };

    const handleOpenFullFormTimestamp = () => {
        if (props.handleOpenFullFormTimestamp) props.handleOpenFullFormTimestamp();
    };

    const buttons = () => {
        const buttons = [];

        if (canEditTriage) {
            if (props.patient?.triage?.triageClinicians && !props.patient?.triage?.discriminator) {
                buttons.push(
                    <AgyleButton variant="contained" onClick={openRemoveTimestamp}>
                        {t('Remove Timestamp')}
                    </AgyleButton>,
                );
            }

            if (referralReviewForm && fullFormMode && hasSRFlag) {
                buttons.push(<ReferralReviewButton small shortText={isSmall} />);
            }

            if (!props.ambulanceHandOverOpened && fullFormMode && ambulance?.includes(props?.patient?.admission?.arrivalModeCode)) {
                buttons.push(
                    <AgyleButton variant="contained" disabled={props.workflowDisabled} onClick={openAmbulanceHandover}>
                        {t('Ambulance Handover form')}
                    </AgyleButton>,
                );
                // buttons.push(renderAmbulanceHandoverForm());
            }

            if (triageTreatmentForm && fullFormMode) {
                buttons.push(
                    <AgyleButton variant="contained" disabled={props.workflowDisabled} onClick={openTreatmentPlan}>
                        {t('Treatment Plan Form')}
                    </AgyleButton>,
                );
            }
        }

        if (!fullFormMode) {
            buttons.push(
                <AgyleButton variant="contained" color="primary" onClick={handleOpenFullFormTimestamp}>
                    {t('Open Form')}
                </AgyleButton>,
            );
            buttons.push(
                <AgyleButton variant="contained" color="primary" onClick={sendChange}>
                    {t('Submit')}
                </AgyleButton>,
            );
        }
        return buttons;
    };

    if (!props.open) return null;

    return (
        <>
            <Grid container spacing={2} alignItems="center" className={clsx(!fullFormMode && 'action-padding')}>
                {!fullFormMode && (
                    <Grid item xs={12}>
                        {patientBanner(props.patient, false)}
                    </Grid>
                )}

                {fullFormMode && (
                    <Grid item xs={12}>
                        <ObservationTable patient={props.patient} />
                    </Grid>
                )}

                <Grid item xs={12}>
                    <InitialAssessmentSummary patient={props.patient} />
                </Grid>

                <Grid item xs={12}>
                    <Triage
                        canEditTriage={canEditTriage}
                        patient={props.patient}
                        patientType={props.patientAgeType}
                        handleLastObs={props.handleLastObs}
                        saveFlag={setFlags}
                        isError={isError}
                        saveInjury={setInjuryForm}
                        pickerError={setErrorPicker}
                        injuryPickerError={setInjuryErrorPicker}
                        modifyPatient={props.modifyPatient}
                        patientHaveModification={props.patientHaveModification}
                        save={setTriageObject}
                        onSave={props.onSave}
                        triageChange={setTriageChange}
                        lastNutrition={lastNutritionValue}
                        fullFormMode={fullFormMode}
                        setEscortList={handleChangeEscort}
                        escortList={escortList}
                        pastAttendances={props.pastAttendances}
                        isSmall={isSmall}
                    />
                </Grid>

                {fullFormMode && props.patientAgeType === PATIENT_AGE_TYPE.PAEDS && (
                    <Grid item xs={12}>
                        {PeadSafeguardingComponent}
                    </Grid>
                )}

                {fullFormMode && props.patientAgeType === PATIENT_AGE_TYPE.OVER75 && (
                    <Grid item xs={12}>
                        {rockwoodComponent}
                    </Grid>
                )}

                {fullFormMode && props.patientAgeType === PATIENT_AGE_TYPE.OVER75 && (
                    <Grid item xs={12}>
                        {DeliriumComponent}
                    </Grid>
                )}

                {fullFormMode && (
                    <Grid item xs={12}>
                        {props.patientAgeType === PATIENT_AGE_TYPE.PAEDS &&
                            dispatch(hasPermission(Permissions.AGYLE_CAN_MANAGE_HOUSEHOLD)) &&
                            HouseholdComponent}
                    </Grid>
                )}
            </Grid>

            <MessageBox
                title={t('Confirmation')}
                haveAccept={{ yes: t('Yes'), no: t('No') }}
                onAccept={handleRemoveTimestamp}
                onClose={closeRemoveTimestamp}
                message={t('Are you sure you want to remove triage timestamp?')}
                open={isRemoveTimestampOpen}
            />

            <AgyleDialog
                open={isConfirmCloseOpen}
                title={t('Warning')}
                fullWidth
                onClose={closeConfirmClose}
                cancelText={t('Cancel')}
                buttons={[
                    <AgyleButton onClick={sendChange} variant="contained" key="save- triage">
                        {t('Close and Save Triage')}
                    </AgyleButton>,
                ]}
                onSave={() => {
                    closeConfirmClose();
                    props.onClose();
                }}
                submitText={t('Close Triage Without Saving')}
                warning
            >
                <InformationBox type="warning">
                    {t('It looks like you have been editing something. If you leave before saving, your changes will be lost.')}
                </InformationBox>
            </AgyleDialog>
        </>
        // </>
    );
};

export { TriageForm };
